/**
 * @file FormulationModal.js
 * @version 3.9.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-05
 * @modified 2026-11-04
 * @description Add / edit / view a formulation.
 *
 * VIEW mirrors the PDF spec sheet: ingredients first, ordered by
 * proportion with zero/unset hidden, then analysis. A date
 * dropdown lists every date the product has data for, newest
 * first, defaulting to the newest (or the newest within `period`
 * when a caller passes one).
 *
 * EDIT is a vertical list — ingredients above analysis, both
 * alphabetical, every component shown so any of them can be filled
 * in. New components can be appended to either list and become
 * available to every formulation, past and future.
 *
 * Every entry has a DATE, the user's (Cam, 2026-10-14): Add asks
 * for one (empty, required, not in the future); Edit shows the date
 * of the entry being edited and lets it change. New figures or
 * notes save a NEW entry on the date in the field; the date alone
 * moves that entry. The period follows the date. The field says
 * which will happen before Save (utils/formulationDates.js).
 *
 * The edit form (FormulationEditor) and its two helpers are exported for the
 * one-step "New formulation for a customer" (NewCustomerFormulationModal),
 * which Add mode can open when the caller passes `newForCustomer`.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import ConfirmDialog from './ConfirmDialog';
import { CustomerLinkBadges, LINK_SOURCES } from './PricingBadges';
import FormulationScreenshotModal from './FormulationScreenshotModal';
import { useSlideOutButton } from './SlideOutButton';
import DateInput from './DateInput';
import { getAccountName } from '../utils/accountCodes';
import { formatExact } from '../utils/numberFormatters';
import {
  londonDayOf, todayLondonDay, formatFormulationDay, formulationDateProblem,
  compareIterationsNewestFirst, editOutcome
} from '../utils/formulationDates';
import './Formulations.css';

/** An entry's date, as the user chose it — DD/MM/YYYY, no time. */
const entryDay = (it) => it?.iteration_date || londonDayOf(it?.created_at);
const formatEntryDate = (it) => formatFormulationDay(entryDay(it));

const byLabel = (a, b) => a.label.localeCompare(b.label, 'en-GB', { sensitivity: 'base' });

// Scope filtering is deliberately NOT applied any more.
//
// Components carry a `scopes` array so a blend's form could hide compound-only
// ingredients and vice versa. In practice the ranges overlap constantly, and
// hiding a field only meant someone couldn't record a figure they had in front
// of them. Every component is now offered on every formulation.
//
// It has to go from the VIEW as well as the edit form. View and PDF already
// drop anything unset, so a scope filter on top of that could only ever hide a
// value someone had deliberately entered — which is precisely the failure the
// old fail-open rules were written to avoid. The server's buildSheet mirrors
// this; see services/formulationService.js.

// Body and footer are separate components so useSlideOutButton fires in the
// same render cycle as the buttons it measures — Modal.js adds its portal
// content on a second cycle, so a hook called from an always-mounted parent
// would find nothing to measure.
function FormulationModalFooter({ mode, saving, onClose, onSave, onEdit, onHistory, onExport, canSave }) {
  useSlideOutButton([mode, saving]);
  const isView = mode === 'view';

  return (
    <>
      <div className="buttons slideout">
        <div>
          <button className="btn-slideout" onClick={onHistory} disabled={!onHistory} title={onHistory ? 'View change history' : 'Not saved yet'}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">history</span>
            </div>
            <span className="btn-label">History</span>
          </button>
        </div>
        <div>
          <button className="btn-slideout" onClick={onExport} disabled={!onExport} title={onExport ? 'Export as PDF' : 'Not saved yet'}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">ios_share</span>
            </div>
            <span className="btn-label">Export</span>
          </button>
        </div>
      </div>
      <div className="footer-right buttons slideout">
        <div>
          <button className="btn-round btn-danger" onClick={onClose} disabled={saving}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">{isView ? 'Close' : 'Cancel'}</span>
          </button>
        </div>
        <div>
          {isView ? (
            <button className="btn-slideout" onClick={onEdit}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">edit</span>
              </div>
              <span className="btn-label">Edit</span>
            </button>
          ) : (
            <button className="btn-slideout" onClick={onSave} disabled={saving || !canSave}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">save</span>
              </div>
              <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/*
 * Is this analysis figure a percentage?
 *
 * Derived from the LABEL, not from `unit`. The unit column says '%' for 30 of
 * the 40 analysis components including CU (mg/Kg), RUFAL g/kg and VIT A kiu/kg,
 * so trusting it would put most of the non-percentage figures in with the
 * percentages. The labels are reliable — they carry their own unit, which is
 * also why nothing appends `unit` when rendering them.
 *
 * A label naming a real unit is not a percentage; otherwise a '%' in the label
 * (or, failing that, the unit) decides. EECFIBRE has neither marker and is a
 * percentage, which is the case the unit fallback is there for.
 */
const NAMED_UNIT = /(g\s*\/\s*kg|mg\s*\/\s*kg|k?iu\s*\/\s*kg|mj\s*\/\s*kg|\bindex\b)/i;

function isPercentAnalysis(component) {
  const label = String(component?.label || '');
  if (NAMED_UNIT.test(label)) return false;
  return label.includes('%') || component?.unit === '%';
}

/**
 * A section that starts closed.
 *
 * The two analysis lists are one field group split in two, not two unrelated
 * things, so the heading reads as a continuation and the count is on it — a
 * collapsed section with no indication of what is inside is a section nobody
 * opens.
 */
/**
 * A collapsed, expandable section — "Further analysis", and now Supplements and
 * Trace elements, which a milk powder label carries as their own blocks.
 *
 * `forceOpenKey` exists because a field created from the picker can land in a
 * closed section, and a closed section does not mount its children: the row
 * appeared nowhere and the focus call quietly hit nothing. When the caller says
 * a key has just been added and it belongs here, the section opens itself.
 */
function CollapsibleSection({ title, count, forceOpen = false, children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  if (!count) return null;
  return (
    <div className={`fm-further${open ? ' is-open' : ''}`}>
      <button type="button" className="fm-further-toggle" onClick={() => setOpen(!open)}>
        <span className="material-symbols-rounded">{open ? 'expand_less' : 'expand_more'}</span>
        <span>{title}</span>
        <span className="fm-further-count">{count}</span>
      </button>
      {open && <div className="fm-further-body">{children}</div>}
    </div>
  );
}

/** Read-only spec sheet — the on-screen twin of the PDF. */
function FormulationView({ components, form, notes }) {
  const num = (key) => {
    const raw = form[key];
    if (raw === '' || raw === undefined || raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  // Ingredients: proportion order, zero and unset both dropped.
  const ingredients = useMemo(() => components
    .filter(c => c.component_group === 'ingredients')
    .map(c => ({ c, value: num(c.key) }))
    .filter(x => x.value !== null && x.value !== 0)
    .sort((a, b) => b.value - a.value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [components, form]);

  // Analysis: unset dropped, but an explicit 0 is a real measurement and stays.
  const analysis = useMemo(() => components
    .filter(c => c.component_group === 'analysis')
    .map(c => ({ c, value: num(c.key) }))
    .filter(x => x.value !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [components, form]);

  // A milk powder label's own two blocks. Same rule as analysis: an explicit
  // zero is a measurement and stays; unset is dropped.
  const supplements = useMemo(() => components
    .filter(c => c.component_group === 'supplements')
    .map(c => ({ c, value: num(c.key) }))
    .filter(x => x.value !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [components, form]);

  const traceElements = useMemo(() => components
    .filter(c => c.component_group === 'trace_elements')
    .map(c => ({ c, value: num(c.key) }))
    .filter(x => x.value !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [components, form]);

  const percentAnalysis = useMemo(
    () => analysis.filter(x => isPercentAnalysis(x.c)), [analysis]);
  const otherAnalysis = useMemo(
    () => analysis.filter(x => !isPercentAnalysis(x.c)), [analysis]);

  const ingredientTotal = ingredients.reduce((s, x) => s + x.value, 0);

  const renderRows = (list) => list.map(({ c, value }) => (
    <div className="fm-spec-row" key={c.key}>
      <span className="fm-spec-label">{c.label}</span>
      <span className="fm-spec-value">{formatExact(value)}</span>
    </div>
  ));

  if (!ingredients.length && !analysis.length && !supplements.length && !traceElements.length) {
    return <p className="fm-spec-empty">No formulation data recorded for this date.</p>;
  }

  return (
    <div className="fm-spec">
      {ingredients.length > 0 && (
        <section className="fm-spec-section">
          <h3 className="fm-spec-title">Ingredients</h3>
          {renderRows(ingredients)}
          <div className="fm-spec-row fm-spec-total">
            <span className="fm-spec-label">Total</span>
            <span className="fm-spec-value">{formatExact(ingredientTotal)}</span>
          </div>
        </section>
      )}

      {(supplements.length > 0 || traceElements.length > 0) && (
        <section className="fm-spec-section">
          <h3 className="fm-spec-title">Supplements</h3>
          {/* A milk powder label's own two blocks. Collapsed like Further
              analysis: it is a long list of statutory detail that matters when
              you go looking for it and is noise the rest of the time. */}
          <CollapsibleSection title="Supplements & vitamins" count={supplements.length}>
            {renderRows(supplements)}
          </CollapsibleSection>
          <CollapsibleSection title="Trace elements" count={traceElements.length}>
            {renderRows(traceElements)}
          </CollapsibleSection>
        </section>
      )}

      {analysis.length > 0 && (
        <section className="fm-spec-section">
          <h3 className="fm-spec-title">Analysis</h3>
          {renderRows(percentAnalysis)}
          {/* The percentages are what anyone reads first; g/kg, mg/kg, MJ/kg
              and the indices are a second question and start out of the way. */}
          <CollapsibleSection title="Further analysis" count={otherAnalysis.length}>
            {renderRows(otherAnalysis)}
          </CollapsibleSection>
        </section>
      )}

      {notes && (
        <div className="fm-spec-notes">
          <span className="fm-spec-notes-label">Notes</span>
          <span>{notes}</span>
        </div>
      )}
    </div>
  );
}

/**
 * One editable list (ingredients or analysis).
 *
 * Rows appear for fields that are actually in use, and a searchable picker adds
 * the rest on demand. The alternative — a labelled input for every component in
 * the registry — meant scrolling ~58 rows to fill in six, and got worse every
 * time anyone added a component.
 *
 * "In use" is deliberately "has a value OR was added this session", not just
 * "has a value": a row you just added sits empty until you type into it, and it
 * must not vanish under the cursor.
 */
function EditSection({ title, group, components, form, setValue, onAddComponent, adding }) {
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [justAdded, setJustAdded] = useState(null);
  const [added, setAdded] = useState(() => new Set());

  const cols = useMemo(
    () => components.filter(c => c.component_group === group).sort(byLabel),
    [components, group]
  );

  const has = useCallback((key) => {
    const v = form[key];
    return (v !== undefined && v !== null && v !== '') || added.has(key);
  }, [form, added]);

  const shown = useMemo(() => cols.filter(c => has(c.key)), [cols, has]);

  // Analysis splits into percentages and everything else; ingredients do not
  // split at all, so that group keeps its whole list in the first slot.
  const isAnalysis = group === 'analysis';
  const shownMain = useMemo(
    () => (isAnalysis ? shown.filter(isPercentAnalysis) : shown), [shown, isAnalysis]);
  const shownFurther = useMemo(
    () => (isAnalysis ? shown.filter(c => !isPercentAnalysis(c)) : []), [shown, isAnalysis]);

  const options = useMemo(
    // No unit badge: the labels already carry their unit ("Barley %",
    // "ME (MJ/Kg)"), so a badge renders as a stray "%" in front of the name.
    () => cols.filter(c => !has(c.key)).map(c => ({
      value: c.key,
      label: c.label,
      searchText: c.label
    })),
    [cols, has]
  );

  const addRow = useCallback((key) => {
    if (!key) return;
    setAdded(prev => new Set(prev).add(key));
    setJustAdded(key);
    // Focus AND scroll. A row is inserted in label order, so it routinely lands
    // off-screen — and if it belongs in a collapsed section it is not mounted
    // at all until `justAdded` opens that section, which is why this retries on
    // the next frame rather than giving up.
    const reach = (attempt = 0) => {
      const el = document.getElementById(`fm-${key}`);
      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else if (attempt < 5) {
        requestAnimationFrame(() => reach(attempt + 1));
      }
    };
    requestAnimationFrame(() => reach());
  }, []);

  const removeRow = useCallback((key) => {
    setAdded(prev => { const n = new Set(prev); n.delete(key); return n; });
    setValue(key, '');
  }, [setValue]);

  const submitNew = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setError('');
    setCreating(true);
    const result = await onAddComponent(label, group);
    setCreating(false);
    if (typeof result === 'string') { setError(result); return; }
    setNewLabel('');
    // Straight onto the form — creating it from here means you want it here.
    if (result?.key) addRow(result.key);
  };

  // Both nouns happen to take "an" — ingredient, analysis figure.
  const noun = group === 'analysis' ? 'analysis figure' : 'ingredient';

  /* One row, used by both analysis lists and by the ingredient list. */
  const renderEditRow = (col) => (
        <div className="fm-edit-row" key={col.key}>
          {/* No unit appended: the labels already carry it ("Barley %",
              "ME (MJ/Kg)"), so adding col.unit renders "Barley %%". */}
          <label className="fm-edit-label" htmlFor={`fm-${col.key}`}>{col.label}</label>
          <Input
            id={`fm-${col.key}`}
            type="number"
            inputMode="decimal"
            min="0"
            placeholder="—"
            value={form[col.key] ?? ''}
            onChange={(e) => setValue(col.key, e.target.value)}
          />
          <button
            type="button"
            className="btn-icon"
            onClick={() => removeRow(col.key)}
            title={`Remove ${col.label} from this formulation`}
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>
  );

  return (
    <section className="fm-edit-section">
      <h3 className="fm-spec-title">{title}</h3>

      {shownMain.map(renderEditRow)}
      {/* Collapsed on open: a formulation is usually filled in on the
          percentages, and thirty rows of g/kg above the picker made the ones
          people actually use hard to find. */}
      <CollapsibleSection
        title="Further analysis"
        count={shownFurther.length}
        forceOpen={!!justAdded && shownFurther.some((c) => c.key === justAdded)}
      >
        {shownFurther.map(renderEditRow)}
      </CollapsibleSection>

      {!shown.length && (
        <p className="fm-edit-empty">
          Nothing recorded yet — pick an {noun} below to start.
        </p>
      )}

      {/* Pick an existing field, or create one. Creating adds it to the
          registry, so it becomes available on every formulation, past and
          future — not just this one. */}
      <div className="fm-edit-picker">
        <SearchableDropdown
          options={options}
          value={null}
          onChange={addRow}
          placeholder={options.length ? `Add an ${noun}…` : 'Every field is already on the form'}
          searchPlaceholder="Type to search…"
          disabled={!options.length}
        />
      </div>

      <div className="fm-add-component">
        <Input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } }}
          placeholder={`…or create a new ${noun}`}
          icon="add"
          disabled={adding || creating}
        />
        <button
          type="button"
          className="btn-icon"
          onClick={submitNew}
          disabled={adding || creating || !newLabel.trim()}
          title="Create and add to every formulation"
        >
          <span className={`material-symbols-rounded${(adding || creating) ? ' spinning' : ''}`}>
            {(adding || creating) ? 'progress_activity' : 'playlist_add'}
          </span>
        </button>
      </div>
      {error && <div className="fm-add-error">{error}</div>}
    </section>
  );
}

/**
 * The entry's date: required, empty until chosen, today at the latest. Its own
 * component so the Add and Edit forms and the one-step customer formulation
 * all show the same field.
 */
export function FormulationDateField({ date, setDate, hint, error }) {
  const today = todayLondonDay();
  return (
    <div className={`formulation-date-field${error ? ' has-error' : ''}`}>
      <label htmlFor="fm-iteration-date">
        Formulation date <span className="required">*</span>
      </label>
      <DateInput
        id="fm-iteration-date"
        value={date || ''}
        onChange={(e) => setDate(e.target.value)}
        max={today}
        placeholderText="DD/MM/YYYY"
        required
        aria-required="true"
      />
      {error && <div className="fm-add-error">{error}</div>}
      {!error && hint && <div className="fm-date-hint">{hint}</div>}
    </div>
  );
}

/**
 * The formulation's edit form — "Read from a file", the four lists and notes.
 *
 * Exported for the one-step "New formulation for a customer"
 * (NewCustomerFormulationModal), which collects a formulation exactly as Add
 * Formulation does. Measures its own slide-out button ("Read from a file"), so
 * it works in any host.
 */
export function FormulationEditor({
  components, form, setForm, notes, setNotes,
  onAddComponent, adding, extractAvailable, onExtractScreenshot,
  // The entry's date (YYYY-MM-DD, '' until chosen) — required. `dateHint` says
  // what saving will do with it; `dateError` is shown once a save was refused.
  date, setDate, dateHint = null, dateError = null
}) {
  useSlideOutButton([extractAvailable]);
  const setValue = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  return (
    <>
      {setDate && (
        <FormulationDateField date={date} setDate={setDate} hint={dateHint} error={dateError} />
      )}
      {/* Reading a supplier's sheet off a file beats retyping thirty
          rows. Only offered when the server has an API key configured —
          otherwise the button would just fail on click. */}
      {extractAvailable && (
        <button
          type="button"
          className="btn-slideout fm-shot-trigger"
          onClick={onExtractScreenshot}
          title="Read a formulation from a screenshot, PDF, spreadsheet or Word document, then approve the figures"
        >
          <div className="icon-wrapper">
            <span className="material-symbols-rounded">document_scanner</span>
          </div>
          <span className="btn-label">Read from a file</span>
        </button>
      )}
      <EditSection
        title="Ingredients"
        group="ingredients"
        components={components}
        form={form}
        setValue={setValue}
        onAddComponent={onAddComponent}
        adding={adding}
      />
      <EditSection
        title="Analysis"
        group="analysis"
        components={components}
        form={form}
        setValue={setValue}
        onAddComponent={onAddComponent}
        adding={adding}
      />
      <EditSection
        title="Supplements & vitamins"
        group="supplements"
        components={components}
        form={form}
        setValue={setValue}
        onAddComponent={onAddComponent}
        adding={adding}
      />
      <EditSection
        title="Trace elements"
        group="trace_elements"
        components={components}
        form={form}
        setValue={setValue}
        onAddComponent={onAddComponent}
        adding={adding}
      />
      <div className="formulation-notes">
        <label htmlFor="fm-notes">Notes</label>
        <Textarea
          id="fm-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this formulation…"
          rows={3}
        />
      </div>
    </>
  );
}

/**
 * Whether the server can read a formulation off a file (it needs an API key).
 * Asked once, rather than offering a button that can only fail.
 */
export function useFormulationExtractAvailable() {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/formulations/extract/status`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setAvailable(!!d.available); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return available;
}

/**
 * Add a component to the registry. It becomes available to every formulation,
 * past and future. Resolves to the created component, or to a string — the
 * error — on failure.
 */
export function useAddFormulationComponent(onComponentsChanged) {
  const [adding, setAdding] = useState(false);
  const addComponent = async (label, group) => {
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/formulations/components`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Unscoped: every component is offered on every formulation now, so
        // stamping the current range onto a new one would be inert at best and
        // misleading at worst.
        body: JSON.stringify({ label, component_group: group, scopes: null })
      });
      const data = await res.json();
      if (!res.ok) return data.error || 'Failed to add';
      // Refresh the registry in the parent so the new row appears here and
      // everywhere else without a reload.
      if (onComponentsChanged) await onComponentsChanged();
      // The created component goes back so the caller can put it straight on
      // the form — a string return means failure, an object means success.
      return data.component || null;
    } catch (err) {
      return err.message;
    } finally {
      setAdding(false);
    }
  };
  return { adding, addComponent };
}

function FormulationModalBody({
  mode, components, form, setForm, notes, setNotes, error, notice,
  itemOptions, itemId, onPickItem, existing, queueProgress,
  iterations, selectedIterationId, onSelectIteration,
  onDeleteIteration, deleting,
  onAddComponent, adding,
  extractAvailable, onExtractScreenshot,
  newForCustomer,
  date, setDate, dateHint, dateError
}) {
  useSlideOutButton([mode, extractAvailable, !!newForCustomer]);
  const isView = mode === 'view';

  // Newest first (the later day, then the later-saved); an older entry on the
  // same day as a newer one says so, since the two would otherwise read alike.
  const dateOptions = useMemo(() => {
    const seenDays = new Set();
    return [...iterations].sort(compareIterationsNewestFirst).map(it => {
      const day = entryDay(it);
      const earlier = seenDays.has(day);
      seenDays.add(day);
      return {
        value: it.id,
        label: `${formatFormulationDay(day)}${it.period_name ? ` — ${it.period_name}` : ''}`
          + (earlier ? ' (earlier entry that day)' : '')
      };
    });
  }, [iterations]);

  const selectedIteration = iterations.find(it => it.id === selectedIterationId) || null;
  // The last entry stays: a formulation with none disappears from the search
  // table (which inner-joins the latest one) while its record lives on. The
  // server enforces this too — this only keeps the button from offering it.
  const deleteDisabled = mode === 'edit' || iterations.length <= 1 || deleting || !selectedIteration;
  const deleteTitle = mode === 'edit'
    ? 'Finish or cancel editing first'
    : iterations.length <= 1
      ? 'The only entry for this formulation — delete the formulation itself instead'
      : 'Delete this entry';

  return (
    <div className="formulation-modal-body">
      {error && <div className="formulation-error">{error}</div>}
      {notice && <div className="formulation-notice">{notice}</div>}

      <div className="formulation-item-picker">
        <label>
          Product
          {queueProgress && (
            <span className="fm-queue-progress">
              {' '}· {queueProgress.index} of {queueProgress.total} to add
            </span>
          )}
        </label>
        {mode === 'add' ? (
          <>
            <SearchableDropdown
              options={itemOptions}
              value={itemId}
              onChange={onPickItem}
              placeholder="Select an inventory item…"
              searchPlaceholder="Search by code or name…"
            />
            {/* A customer's new blend usually has no item yet. Rather than
                Add Item, then this, then Assign to customer, one window does
                all three. Offered only by callers that can open it. */}
            {newForCustomer && (
              <div className="fm-new-for-customer">
                <span className="fm-new-for-customer-hint">No item yet?</span>
                <button
                  type="button"
                  className="btn-slideout"
                  onClick={newForCustomer.onOpen}
                  disabled={!!newForCustomer.disabledReason}
                  title={newForCustomer.disabledReason
                    || 'Create the item, its formulation and its place under a customer in one go'}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">person_add</span>
                  </div>
                  <span className="btn-label">New formulation for a customer</span>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="formulation-item-locked" style={{ justifyContent: 'space-between' }}>
            <div>
              <span className="account-badge-inline">{existing?.item_code}</span>
              <span className="fm-locked-name" style={{ fontSize: '0.8em' }}>{existing?.item_name}</span>
              <CustomerLinkBadges links={existing?.customer_links} />
            </div>
            <div>
              {existing?.sales_account_code && (
                <span className="account-badge-inline">{existing.sales_account_code}</span>
              )}
              <span className="fm-locked-account">{getAccountName(existing?.sales_account_code) || ''}</span>
            </div>
          </div>
        )}
      </div>

      {/* Spelled out rather than left to the badge's tooltip: knowing whose
          recipe this is matters when deciding what may go on a sheet sent to a
          mill, and a hover isn't good enough for that. */}
      {mode !== 'add' && existing?.customer_links?.length > 0 && (
        <div className="formulation-linked-customers">
          <label>Priced for</label>
          <ul className="linked-customer-list">
            {existing.customer_links.map((l, idx) => {
              const cfg = LINK_SOURCES[l.source] || { icon: 'link', label: l.source };
              return (
                <li key={`${l.account_id}-${l.source}-${idx}`} className={l.is_archived ? 'is-archived' : undefined}>
                  <span className="material-symbols-rounded" title={cfg.label}>{cfg.icon}</span>
                  <span className="account-badge-inline">{l.account_no}</span>
                  <span className="linked-customer-name">{l.account_name}</span>
                  <span className="linked-customer-source">
                    {cfg.label}{l.is_archived ? ' · archived' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Every date this product has data for. Values are already loaded, so
          switching is instant. Locked while editing — the form holds unsaved
          changes that swapping the underlying iteration would silently discard. */}
      {mode !== 'add' && iterations.length > 0 && (
        <div className="formulation-date-picker">
          <label>{mode === 'edit' ? 'Editing the entry dated' : 'Formulation date'}</label>
          {/* Delete sits beside the picker rather than in the footer because it
              acts on the entry the picker is showing, not on the formulation.
              Always rendered — disabled with a reason when it can't apply. */}
          <div className="formulation-date-row">
            <Dropdown
              options={dateOptions}
              value={selectedIterationId}
              onChange={(id) => onSelectIteration(iterations.find(it => it.id === id))}
              disabled={mode === 'edit'}
            />
            <button
              type="button"
              className={`btn-icon${deleteDisabled ? ' btn-disabled' : ''}`}
              onClick={() => onDeleteIteration(selectedIteration)}
              disabled={deleteDisabled}
              title={deleteTitle}
              aria-label="Delete this formulation entry"
            >
              <span className="material-symbols-rounded">
                {deleting ? 'hourglass_empty' : 'delete'}
              </span>
            </button>
          </div>
        </div>
      )}

      {isView ? (
        <FormulationView components={components} form={form} notes={notes} />
      ) : (
        <>
          <FormulationEditor
            components={components}
            form={form}
            setForm={setForm}
            notes={notes}
            setNotes={setNotes}
            onAddComponent={onAddComponent}
            adding={adding}
            extractAvailable={extractAvailable}
            onExtractScreenshot={onExtractScreenshot}
            date={date}
            setDate={setDate}
            dateHint={dateHint}
            dateError={dateError}
          />
        </>
      )}
    </div>
  );
}

function FormulationModal({
  // currentPeriod is no longer read: an edit is dated by the user, not by
  // today's period. Callers may still pass it.
  mode: initialMode, formulationId, components, period,
  onComponentsChanged, onClose, onSaved, onExport, onIterationDeleted,
  // Opened from a list of items that need one — the Processor Report walks its
  // missing formulations this way. The item is pre-picked so the user is not
  // asked to find, in a dropdown of three thousand, the item they just clicked.
  initialItemId = null, queueProgress = null,
  // { onOpen, disabledReason } — offers "New formulation for a customer" in add
  // mode (NewCustomerFormulationModal). Absent: not offered.
  newForCustomer = null
}) {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(initialMode !== 'add');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [existing, setExisting] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [selectedIterationId, setSelectedIterationId] = useState(null);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState('');
  // The entry's date (YYYY-MM-DD). Empty on Add — the user chooses it; on
  // Edit, the date of the entry being edited until they change it.
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [itemId, setItemId] = useState(null);
  // Screenshot extraction needs an API key on the server. Ask once rather than
  // offering a button that can only fail.
  const extractAvailable = useFormulationExtractAvailable();
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [items, setItems] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editingId, setEditingId] = useState(formulationId);
  // Which product range's components to show. Comes from the saved formulation
  // once there is one; before that, from the item chosen in the picker.

  const valuesToForm = (values) =>
    Object.fromEntries(Object.entries(values || {}).map(([k, v]) => [k, String(v)]));

  const applyIteration = useCallback((iteration) => {
    if (!iteration) return;
    setSelectedIterationId(iteration.id);
    setForm(valuesToForm(iteration.component_values));
    setNotes(iteration.notes || '');
    setDate(entryDay(iteration) || '');
    setDateError('');
  }, []);

  const loadFormulation = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/formulations/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load formulation');
      setExisting(data.formulation);
      const list = [...(data.iterations || [])].sort(compareIterationsNewestFirst);
      setIterations(list);
      // Default to the newest entry within the period a caller asked for;
      // fall back to the newest overall if this product has none there.
      const inPeriod = period ? list.filter(it => it.period_name === period) : [];
      applyIteration(inPeriod[0] || list[0] || null);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [period, applyIteration]);

  useEffect(() => {
    if (formulationId) loadFormulation(formulationId);
  }, [formulationId, loadFormulation]);

  // ─── Item picker options (add mode) ───────────────────────────────────────
  const loadItems = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/formulations/items?limit=5000`, {
        credentials: 'include'
      });
      const data = await res.json();
      // Don't fall back to an empty list on failure — that renders as "no
      // items exist", which is indistinguishable from a broken endpoint.
      if (!res.ok) throw new Error(data.error || 'Failed to load inventory items');
      setItems(data.items || []);
      setItemOptions((data.items || []).map(i => ({
        value: i.id,
        label: i.formulation_id ? `${i.item_name} (has formulation)` : i.item_name,
        badge: i.item_code,
        searchText: `${i.item_code || ''} ${i.item_name || ''}`
      })));
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (mode === 'add') loadItems();
  }, [mode, loadItems]);

  // Picking an item that already has a formulation opens that one for editing
  // rather than failing the create with a 409.
  const handlePickItem = async (pickedId) => {
    setItemId(pickedId);
    setError('');
    const picked = items.find(i => i.id === pickedId);
    if (!picked?.formulation_id) return;

    const data = await loadFormulation(picked.formulation_id);
    if (data) {
      setEditingId(picked.formulation_id);
      setMode('edit');
      setNotice(`${picked.item_code} already has a formulation — opened it for editing.`);
    }
  };

  // Pre-pick the item a caller handed us, once the list it lives in has
  // arrived. It goes through handlePickItem rather than setItemId so an item
  // that turns out to HAVE a formulation still opens for editing instead of
  // failing the save with a 409.
  useEffect(() => {
    if (mode !== 'add' || !initialItemId || itemId || !items.length) return;
    handlePickItem(initialItemId);
    // handlePickItem is recreated each render; the guards above make one run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialItemId, itemId, items.length]);

  const startEdit = () => {
    setMode('edit');
    setNotice('');
  };

  // What Save will do with the date, said before it is pressed.
  const selectedIteration = iterations.find(it => it.id === selectedIterationId) || null;
  const dateHint = useMemo(() => {
    if (mode !== 'edit' || !selectedIteration || !date) return null;
    const blankStripped = (o) => Object.fromEntries(
      Object.entries(o || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        .map(([k, v]) => [k, Number(v)])
    );
    const a = blankStripped(form);
    const b = blankStripped(selectedIteration.component_values);
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    const figuresChanged = [...keys].some(k => a[k] !== b[k])
      || (notes || '') !== (selectedIteration.notes || '');
    const baseDay = entryDay(selectedIteration);
    const outcome = editOutcome({ figuresChanged, baseDay, day: date });
    if (outcome === 'redate') {
      return `Saving moves this entry from ${formatFormulationDay(baseDay)} to ${formatFormulationDay(date)}.`;
    }
    if (outcome === 'new') {
      return `Saving adds a new entry dated ${formatFormulationDay(date)}; the entry dated ${formatFormulationDay(baseDay)} stays in the history.`;
    }
    return 'Change the figures or notes to save a new entry, or the date to move this one.';
  }, [mode, selectedIteration, date, form, notes]);

  // No client-side scope any more. It used to decide which components this form
  // offered; every component is offered on every formulation now, and the
  // formulation's own range is derived server-side from the item's sales
  // account code (scopeForAccount) rather than sent from here.

  // ─── Add a component to the registry ──────────────────────────────────────
  const { adding, addComponent: handleAddComponent } = useAddFormulationComponent(onComponentsChanged);

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError('');
    // Required, a real day, not in the future — the server says the same.
    const dateProblem = formulationDateProblem(date);
    setDateError(dateProblem || '');
    if (dateProblem) {
      setError(dateProblem);
      return;
    }
    setSaving(true);
    try {
      // Blanks are stripped so "not measured" stays distinct from an explicit 0.
      const component_values = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );

      const isCreate = !editingId;
      const res = await fetch(
        isCreate ? `${API_URL}/api/formulations` : `${API_URL}/api/formulations/${editingId}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(
            isCreate
              ? { inventory_item_id: itemId, component_values, notes, iteration_date: date }
              // The entry the edit was made on, so the server knows whether the
              // date alone moved (re-date) or the figures changed (new entry).
              : { component_values, notes, iteration_id: selectedIterationId, iteration_date: date }
          )
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save formulation');
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Which entry the product falls back to. Only meaningful when the newest is
  // the one going (iterations are ordered newest first) — delete an older entry
  // and what the sheet prints doesn't change at all.
  const nextAfterDelete = confirmDelete && confirmDelete.id === iterations[0]?.id
    ? iterations.find(it => it.id !== confirmDelete.id) || null
    : null;

  // ─── Delete one entry ─────────────────────────────────────────────────────
  // Undoes an update made in error: with the newest entry gone, the one before
  // it becomes current again. The server refuses to remove the last one.
  const handleDeleteIteration = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target || !editingId) return;
    setError('');
    setDeleting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/formulations/${editingId}/iterations/${target.id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete entry');
      // Reload rather than splice the list locally: which entry becomes current
      // is the server's call, and the modal should show what it decided.
      await loadFormulation(editingId);
      setNotice(`Deleted the entry dated ${formatEntryDate(target)}.`);
      // The search table shows the latest entry per product, so it's now stale.
      if (onIterationDeleted) onIterationDeleted();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Just the action — the product and its period are both already visible in
  // the item picker and date dropdown immediately below.
  const title = mode === 'add'
    ? 'Add Formulation'
    : `${mode === 'edit' ? 'Edit' : 'View'} Formulation`;

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={title}
        className="formulation"
        closeOnOverlayClick={mode === 'view'}
        footer={loading ? null : (
          <FormulationModalFooter
            mode={mode}
            saving={saving}
            canSave={mode !== 'add' || !!itemId}
            onClose={onClose}
            onSave={handleSave}
            onEdit={startEdit}
            onHistory={editingId ? () => setShowHistory(true) : null}
            // The period of the entry on screen, so the sheet is the one being
            // looked at — not whatever the caller's period happens to hold.
            onExport={editingId && onExport
              ? () => onExport(editingId, (iterations.find(it => it.id === selectedIterationId) || {}).period_name || null)
              : null}
          />
        )}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <FormulationModalBody
            mode={mode}
            components={components}
            form={form}
            setForm={setForm}
            notes={notes}
            setNotes={setNotes}
            error={error}
            notice={notice}
            itemOptions={itemOptions}
            itemId={itemId}
            onPickItem={handlePickItem}
            existing={existing}
            queueProgress={queueProgress}
            iterations={iterations}
            selectedIterationId={selectedIterationId}
            onSelectIteration={applyIteration}
            onDeleteIteration={(it) => setConfirmDelete(it)}
            deleting={deleting}
            onAddComponent={handleAddComponent}
            adding={adding}
            extractAvailable={extractAvailable}
            onExtractScreenshot={() => setShowScreenshot(true)}
            newForCustomer={newForCustomer}
            date={date}
            setDate={(d) => { setDate(d); setDateError(''); }}
            dateHint={dateHint}
            dateError={dateError}
          />
        )}
      </Modal>

      {showScreenshot && (
        <FormulationScreenshotModal
          components={components}
          existingValues={form}
          onCreateComponent={handleAddComponent}
          onClose={() => setShowScreenshot(false)}
          onApply={(values) => {
            // Merge rather than replace: the reviewer may have typed some rows
            // already, and only the ones they ticked come through here.
            setForm((prev) => ({ ...prev, ...values }));
            setShowScreenshot(false);
            setNotice(
              `Applied ${Object.keys(values).length} figure(s) from the file. ` +
              'Check them, then save to record the formulation.'
            );
          }}
        />
      )}

      {/* Names the date being removed and the one that replaces it — the entries
          differ only by their figures, so "are you sure?" alone wouldn't tell
          the user which of them they're about to lose. */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        type="error"
        title="Delete formulation entry"
        message={
          confirmDelete
            ? `Delete the ${existing?.item_code || ''} entry dated ${formatEntryDate(confirmDelete)}?` +
              (nextAfterDelete
                ? ` The entry dated ${formatEntryDate(nextAfterDelete)} becomes the current one.`
                : '') +
              ' This cannot be undone.'
            : ''
        }
        confirmText="Delete"
        onConfirm={handleDeleteIteration}
        onCancel={() => setConfirmDelete(null)}
      />

      {showHistory && existing && (
        <AuditHistoryModal
          isOpen={true}
          onClose={() => setShowHistory(false)}
          entityType="formulation"
          entityId={existing.id}
          entityIdentifier={`${existing.item_code} - ${existing.item_name}`}
        />
      )}
    </>
  );
}

export default FormulationModal;
